<template>
  <!-- Bestandsuebersicht mobil (Trello 21.08.): wie Desktop Inventories.vue, kompakt in 5 Spalten -->
  <div class="m-list">
    <div class="m-search">
      <i class="material-icons">search</i>
      <input v-model="keywords" :placeholder="$t('mobile.wms.searchGeneral')" type="search" @input="debouncedReload" />
      <button v-if="keywords" class="m-search__clear" @click="clearField('keywords')"><i class="material-icons">close</i></button>
    </div>
    <!-- Foto/Scanner am SKU-Feld (Trello WMS mobil, Update 28.08.): gescannte GTIN loest der Server zur SKU auf -->
    <div class="m-search-row">
      <div class="m-search">
        <i class="material-icons">qr_code_2</i>
        <input v-model="sku" :placeholder="$t('mobile.wms.searchSku')" type="search" @input="debouncedReload" />
        <button v-if="sku" class="m-search__clear" @click="clearField('sku')"><i class="material-icons">close</i></button>
      </div>
      <m-wms-photo-button kind="ean" @extracted="onPhotoResult" />
      <button type="button" class="m-wms-form__iconbtn" @click="scannerOpen = true"><i class="material-icons">qr_code_scanner</i></button>
    </div>
    <m-barcode-scanner v-if="scannerOpen" @scanned="onScanned" @close="scannerOpen = false" />
    <div class="m-wms-toggles">
      <label><input v-model="exactMatchSku" type="checkbox" @change="reload" /> {{ $t("mobile.wms.exactMatch") }}</label>
      <label><input v-model="includeBWare" type="checkbox" @change="reload" /> {{ $t("mobile.wms.includeBWare") }}</label>
    </div>

    <div v-if="loading && !items.length" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!items.length" class="m-hint">{{ $t("mobile.noResults") }}</div>

    <table v-if="items.length" class="m-wms-table">
      <thead>
        <tr>
          <th></th>
          <th>{{ $t("mobile.wms.sku") }}</th>
          <th class="text-right">{{ $t("mobile.wms.total") }}</th>
          <th>{{ $t("mobile.wms.perWarehouse") }}</th>
          <th>{{ $t("mobile.wms.sales") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item.sku">
          <td><ProductImage :sku="item.sku" :size="40" /></td>
          <td class="m-wms-table__sku" @click="showName(item)">{{ item.sku }}</td>
          <td class="text-right" @click="openDetails(item)">
            <!-- Gesamt fett, DARUNTER der Zulauf (Karte: "Gesamt -> fetter, darunter in Zulauf") -->
            <div><strong>{{ item.totalInventory }}</strong></div>
            <div class="m-wms-table__sub">{{ $t("mobile.wms.inflow") }}: {{ item.inFlow || 0 }}</div>
          </td>
          <td @click="openDetails(item)">
            <div v-for="w in item.inventories" :key="w.warehouseCode" class="m-wms-table__line">{{ w.warehouseCode }}: {{ w.inventory }}</div>
          </td>
          <td>
            <div class="m-wms-table__line">1d: {{ item.productsShippingLastDay || 0 }}</div>
            <div class="m-wms-table__line">7d: {{ item.productsShipping7Days || 0 }}</div>
            <div class="m-wms-table__line">30d: {{ item.productsShipping30Days || 0 }}</div>
            <div class="m-wms-table__line">90d: {{ item.productsShipping90Days || 0 }}</div>
            <div class="m-wms-table__line">365d: {{ item.productsShipping365Days || 0 }}</div>
          </td>
        </tr>
      </tbody>
    </table>

    <button v-if="items.length < total" class="m-more" :disabled="loading" @click="loadMore">
      {{ loading ? $t("mobile.loading") : $t("mobile.loadMore", { count: total - items.length }) }}
    </button>

    <!-- Klick auf SKU: Name anzeigen -->
    <m-sheet v-if="nameSheet" :title="nameSheet.sku" @close="nameSheet = null">
      <p class="m-wms-name">{{ nameSheet.name || "–" }}</p>
    </m-sheet>

    <!-- Klick auf Gesamt/Lagermengen: Chargen-Detail wie am Desktop -->
    <m-sheet v-if="detailSheet" :title="detailSheet.sku" @close="detailSheet = null">
      <div v-if="detailLoading" class="m-hint">{{ $t("mobile.loading") }}</div>
      <table v-else class="m-wms-table m-wms-table--sheet">
        <thead>
          <tr>
            <th>{{ $t("mobile.wms.location") }}</th>
            <th class="text-right">{{ $t("mobile.wms.inventory") }}</th>
            <th class="text-right">{{ $t("mobile.wms.reserved") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in details" :key="d.id">
            <td>{{ d.fullName || d.storageLocation }}</td>
            <td class="text-right">{{ d.inventory }}</td>
            <td class="text-right">{{ d.reserved }}</td>
          </tr>
        </tbody>
      </table>
    </m-sheet>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import WarehouseService from "@/services/warehouse.service";
import ProductImage from "@/components/Products/ProductImage.vue";
import MSheet from "../components/MSheet.vue";
import MBarcodeScanner from "../components/MBarcodeScanner.vue";
import MWmsPhotoButton from "./MWmsPhotoButton.vue";
import { assignedWarehouse } from "@/mixins/auth";

const PAGE_SIZE = 25;

export default {
  name: "MInventories",
  components: { ProductImage, MSheet, MBarcodeScanner, MWmsPhotoButton },
  data() {
    return {
      items: [],
      total: 0,
      page: 0,
      loading: false,
      keywords: "",
      sku: "",
      scannerOpen: false,
      exactMatchSku: false,
      includeBWare: false,
      nameSheet: null,
      detailSheet: null,
      details: [],
      detailLoading: false,
    };
  },
  created() {
    this.debouncedReload = debounce(this.reload, 350);
    this.reload();
  },
  methods: {
    // Kamera-Scan / Foto: Code ins SKU-Feld, der Server loest eine GTIN zur SKU auf
    onScanned({ value }) {
      this.scannerOpen = false;
      this.sku = String(value || "").trim();
      this.reload();
    },
    onPhotoResult(r) {
      this.sku = (r && (r.sku || r.ean)) || "";
      this.reload();
    },
    clearField(name) {
      this[name] = "";
      this.reload();
    },
    async reload() {
      this.page = 0;
      this.items = [];
      await this.fetch();
    },
    async loadMore() {
      this.page += 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const userWarehouse = assignedWarehouse();
        const res = await WarehouseService.getInventories({
          keywords: this.keywords || undefined,
          sku: this.sku || undefined,
          exactMatchSku: this.exactMatchSku || undefined,
          includeBWare: this.includeBWare || undefined,
          warehouseId: userWarehouse ? userWarehouse.id : undefined,
          page: this.page,
          pageSize: PAGE_SIZE,
        });
        this.items = this.page === 0 ? res.data || [] : this.items.concat(res.data || []);
        this.total = res.total || 0;
      } finally {
        this.loading = false;
      }
    },
    showName(item) {
      this.nameSheet = item;
    },
    async openDetails(item) {
      this.detailSheet = item;
      this.detailLoading = true;
      try {
        this.details = (await WarehouseService.getWarehouseInventoriesBySku(item.sku)) || [];
      } finally {
        this.detailLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./m-wms.scss"></style>
